/**
 * Lead submission — the write side of the gateway.
 *
 * Reads degrade silently into DEMO content (see gateway.ts), but a WRITE must
 * never pretend to have succeeded: if the mutation fails we say so and keep the
 * user's input on screen, rather than showing a thank-you for a lead that was
 * never stored.
 */
import { mutations } from "@mb-group/graphql-client";
import type { ConsentKind, Locale } from "@mb-group/shared-types";
import type { GqlClient } from "../core/deps.js";

/** Version stamped on the consent we record with every submission. */
const PRIVACY_CONSENT_VERSION = "2026-07";

export interface LeadOutcome {
	ok: boolean;
	/** Server-provided message, when there is one. */
	message?: string;
	/** True when the failure looks like a transport/availability problem. */
	unavailable?: boolean;
	/** Set when the gateway recognised the lead as a duplicate. */
	deduplicated?: boolean;
}

interface LeadResultPayload {
	ok: boolean;
	leadId?: string | null;
	deduplicated?: boolean | null;
	message?: string | null;
}

export interface LeadContext {
	client: GqlClient;
	site: string;
	locale: Locale;
}

function consents(
	kinds: ConsentKind[],
): { kind: ConsentKind; version: string; granted: boolean }[] {
	return kinds.map((kind) => ({ kind, version: PRIVACY_CONSENT_VERSION, granted: true }));
}

function classify(error: unknown): LeadOutcome {
	const code =
		error && typeof error === "object" && "code" in error && typeof error.code === "string"
			? error.code
			: undefined;
	const message =
		error && typeof error === "object" && "message" in error && typeof error.message === "string"
			? error.message
			: undefined;

	if (code === "DUPLICATE_LEAD") {
		return { ok: true, deduplicated: true, ...(message ? { message } : {}) };
	}
	// VALIDATION_FAILED / CONSENT_REQUIRED / CAPTCHA_FAILED are the user's to fix;
	// anything else (or no code at all) means the gateway did not answer.
	const userFixable =
		code === "VALIDATION_FAILED" || code === "CONSENT_REQUIRED" || code === "CAPTCHA_FAILED";
	return {
		ok: false,
		unavailable: !userFixable,
		...(message ? { message } : {}),
	};
}

/** Submit a CMS-defined form (contact, callback, …). */
export async function submitForm(
	ctx: LeadContext,
	formKey: string,
	source: string,
	values: Record<string, unknown>,
): Promise<LeadOutcome> {
	try {
		const result = await ctx.client.mutate<{ submitForm: LeadResultPayload }>(
			mutations.SUBMIT_FORM,
			{
				input: {
					site: ctx.site,
					locale: ctx.locale,
					formKey,
					source,
					values,
					consents: consents(["privacy_policy", "data_processing"]),
				},
			},
		);
		const payload = result.submitForm;
		return {
			ok: payload.ok,
			...(payload.message ? { message: payload.message } : {}),
			...(payload.deduplicated ? { deduplicated: true } : {}),
		};
	} catch (error) {
		return classify(error);
	}
}

/** Subscribe an address to the regulatory newsletter. */
export async function subscribeNewsletter(
	ctx: LeadContext,
	email: string,
	newsletterKey = "regulatory",
): Promise<LeadOutcome> {
	try {
		const result = await ctx.client.mutate<{ subscribeNewsletter: LeadResultPayload }>(
			mutations.SUBSCRIBE_NEWSLETTER,
			{
				input: {
					site: ctx.site,
					locale: ctx.locale,
					newsletterKey,
					email,
					consents: consents(["privacy_policy", "marketing_email"]),
				},
			},
		);
		const payload = result.subscribeNewsletter;
		return {
			ok: payload.ok,
			...(payload.message ? { message: payload.message } : {}),
			...(payload.deduplicated ? { deduplicated: true } : {}),
		};
	} catch (error) {
		return classify(error);
	}
}

export interface DownloadOutcome extends LeadOutcome {
	/** Short-lived signed URL, issued only after lead capture + consent. */
	signedUrl?: string;
}

/** Request a gated resource (report, case study, webinar recording). */
export async function requestDownload(
	ctx: LeadContext,
	resourceSlug: string,
	email: string,
	company?: string,
): Promise<DownloadOutcome> {
	try {
		const result = await ctx.client.mutate<{
			requestResourceDownload: { ok: boolean; signedUrl?: string | null; leadId?: string | null };
		}>(mutations.REQUEST_RESOURCE_DOWNLOAD, {
			input: {
				site: ctx.site,
				locale: ctx.locale,
				resourceSlug,
				email,
				...(company ? { company } : {}),
				consents: consents(["privacy_policy", "marketing_email"]),
			},
		});
		const payload = result.requestResourceDownload;
		return {
			ok: payload.ok,
			...(payload.signedUrl ? { signedUrl: payload.signedUrl } : {}),
		};
	} catch (error) {
		return classify(error);
	}
}
